import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import ManagerLayout from '../components/ManagerLayout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Download } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ManagerEditLog = ({ manager, onLogout }) => {
  const [editLogs, setEditLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [filters, setFilters] = useState({
    start_date: '',
    end_date: '',
    action_type: '',
    manager_name: '',
    employee_name: '',
  });
  const [loading, setLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({
    key: 'timestamp',
    direction: 'desc',
  });

  useEffect(() => {
    // Check if user is Administrator
    if (manager?.role !== 'Administrator') {
      toast.error('Access denied. Administrator role required.');
      onLogout();
      return;
    }
    fetchEditLogs();
  }, []);

  useEffect(() => {
    applyFiltersAndSort();
  }, [editLogs, filters, sortConfig]);

  const fetchEditLogs = async () => {
    setLoading(true);
    try {
      const token = sessionStorage.getItem('managerToken');
      const params = new URLSearchParams();

      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);

      const response = await axios.get(`${API}/edit-logs?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setEditLogs(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Access denied. Administrator role required.');
        onLogout();
      } else {
        toast.error('Error fetching edit logs');
      }
    } finally {
      setLoading(false);
    }
  };

  const applyFiltersAndSort = () => {
    let filtered = [...editLogs];

    // Apply filters
    if (filters.action_type) {
      filtered = filtered.filter((log) => log.action_type === filters.action_type);
    }
    if (filters.manager_name) {
      filtered = filtered.filter((log) =>
        log.changed_by_name.toLowerCase().includes(filters.manager_name.toLowerCase())
      );
    }
    if (filters.employee_name) {
      filtered = filtered.filter((log) =>
        log.employee_name.toLowerCase().includes(filters.employee_name.toLowerCase())
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];

      if (sortConfig.key === 'timestamp') {
        aVal = new Date(aVal).getTime();
        bVal = new Date(bVal).getTime();
      } else if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredLogs(filtered);
  };

  const handleSort = (key) => {
    setSortConfig({
      key,
      direction:
        sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc',
    });
  };

  const handleExportCSV = () => {
    try {
      const csvHeaders = [
        'Timestamp',
        'Action Type',
        'Employee Name',
        'Location Name',
        'Original Clock In',
        'Original Clock Out',
        'Updated Clock In',
        'Updated Clock Out',
        'Reason for Change',
        'Changed By Name',
        'Changed By Email',
      ];

      const csvRows = filteredLogs.map((log) => [
        formatDateTime(log.timestamp),
        log.action_type,
        log.employee_name,
        log.location_name,
        formatDateTime(log.original_clock_in),
        formatDateTime(log.original_clock_out),
        formatDateTime(log.updated_clock_in) || '-',
        formatDateTime(log.updated_clock_out) || '-',
        log.reason_for_change,
        log.changed_by_name,
        log.changed_by_email,
      ]);

      const csvContent = [
        csvHeaders.join(','),
        ...csvRows.map((row) =>
          row.map((cell) => `"${cell || ''}"`).join(',')
        ),
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `edit_log_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success('Edit log exported successfully');
    } catch (error) {
      toast.error('Error exporting edit log');
    }
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getActionTypeBadge = (actionType) => {
    const colors = {
      edit: 'bg-yellow-100 text-yellow-800',
      archive: 'bg-blue-100 text-blue-800',
      delete: 'bg-red-100 text-red-800',
    };

    return (
      <span
        className={`px-3 py-1 rounded-full text-xs font-semibold ${
          colors[actionType] || 'bg-gray-100 text-gray-800'
        }`}
      >
        {actionType.charAt(0).toUpperCase() + actionType.slice(1)}
      </span>
    );
  };

  return (
    <ManagerLayout manager={manager} onLogout={onLogout}>
      <div className="p-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-6">Edit Log</h1>

        {/* Filters */}
        <Card className="p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">
            Filter Logs
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Start Date
              </label>
              <Input
                type="date"
                value={filters.start_date}
                onChange={(e) =>
                  setFilters({ ...filters, start_date: e.target.value })
                }
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                End Date
              </label>
              <Input
                type="date"
                value={filters.end_date}
                onChange={(e) =>
                  setFilters({ ...filters, end_date: e.target.value })
                }
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Action Type
              </label>
              <select
                value={filters.action_type}
                onChange={(e) =>
                  setFilters({ ...filters, action_type: e.target.value })
                }
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              >
                <option value="">All Actions</option>
                <option value="edit">Edit</option>
                <option value="archive">Archive</option>
                <option value="delete">Delete</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Employee Name
              </label>
              <Input
                type="text"
                placeholder="Search by employee name"
                value={filters.employee_name}
                onChange={(e) =>
                  setFilters({ ...filters, employee_name: e.target.value })
                }
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Changed By (Manager)
              </label>
              <Input
                type="text"
                placeholder="Search by manager name"
                value={filters.manager_name}
                onChange={(e) =>
                  setFilters({ ...filters, manager_name: e.target.value })
                }
              />
            </div>
            <div className="flex items-end">
              <Button
                onClick={fetchEditLogs}
                className="w-full"
                disabled={loading}
              >
                {loading ? 'Loading...' : 'Apply Date Filters'}
              </Button>
            </div>
          </div>
        </Card>

        {/* Edit Logs Table */}
        <Card className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-800">
              All Changes ({filteredLogs.length} {filteredLogs.length !== editLogs.length && `of ${editLogs.length}`})
            </h2>
            <Button
              onClick={handleExportCSV}
              disabled={filteredLogs.length === 0}
              className="flex items-center gap-2"
            >
              <Download size={16} />
              Export to CSV
            </Button>
          </div>

          {filteredLogs.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <p>No edit logs found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th
                      className="text-left py-3 px-4 text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-50"
                      onClick={() => handleSort('timestamp')}
                    >
                      Timestamp {sortConfig.key === 'timestamp' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th
                      className="text-left py-3 px-4 text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-50"
                      onClick={() => handleSort('action_type')}
                    >
                      Action {sortConfig.key === 'action_type' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th
                      className="text-left py-3 px-4 text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-50"
                      onClick={() => handleSort('employee_name')}
                    >
                      Employee {sortConfig.key === 'employee_name' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th
                      className="text-left py-3 px-4 text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-50"
                      onClick={() => handleSort('location_name')}
                    >
                      Location {sortConfig.key === 'location_name' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">
                      Original Time
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">
                      Updated Time
                    </th>
                    <th
                      className="text-left py-3 px-4 text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-50"
                      onClick={() => handleSort('changed_by_name')}
                    >
                      Changed By {sortConfig.key === 'changed_by_name' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">
                      Reason
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLogs.map((log) => (
                    <tr
                      key={log.id}
                      className="border-b hover:bg-gray-50"
                    >
                      <td className="py-3 px-4 text-sm text-gray-600">
                        {formatDateTime(log.timestamp)}
                      </td>
                      <td className="py-3 px-4">
                        {getActionTypeBadge(log.action_type)}
                      </td>
                      <td className="py-3 px-4 text-gray-800">
                        {log.employee_name}
                      </td>
                      <td className="py-3 px-4 text-gray-600">
                        {log.location_name}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600">
                        <div>In: {formatDateTime(log.original_clock_in)}</div>
                        {log.original_clock_out && (
                          <div>Out: {formatDateTime(log.original_clock_out)}</div>
                        )}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600">
                        {log.action_type === 'edit' ? (
                          <>
                            <div>In: {formatDateTime(log.updated_clock_in)}</div>
                            {log.updated_clock_out && (
                              <div>Out: {formatDateTime(log.updated_clock_out)}</div>
                            )}
                          </>
                        ) : (
                          <span className="text-gray-400 italic">N/A</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-800">
                        <div>{log.changed_by_name}</div>
                        <div className="text-xs text-gray-500">
                          {log.changed_by_email}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600 max-w-xs">
                        {log.reason_for_change}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </ManagerLayout>
  );
};

export default ManagerEditLog;
